import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

/**
 * Hold / resume / cancel a live indent.
 *
 * The approval gate decides whether an indent is ever created. This is what
 * happens afterwards — a client goes quiet and the purchase should pause, or an
 * order is dropped and it should stop counting as money we intend to spend.
 *
 * Only a procurement admin or the configured indent gatekeeper sees these; the
 * server checks the same thing, so hiding the buttons is a courtesy rather than
 * the actual control.
 */

export type IndentLifecycle = {
  holdReason?: string;
  heldAt?: string;
  heldByName?: string;
  resumedAt?: string;
  resumedByName?: string;
  resumeNote?: string;
  cancelReason?: string;
  cancelledAt?: string;
  cancelledByName?: string;
  statusAtChange?: string;
};

const fmt = (d?: string) => (d ? new Date(d).toLocaleString('en-IN') : '');

/**
 * What this user may do to an indent. The server answers per action, because
 * hold / cancel / reinstate / edit are four separate capabilities now — an
 * admin can be allowed to hold a purchase and not to cancel one.
 *
 * `can` is absent on an older backend; fall back to the coarse `allowed` flag
 * so the buttons do not silently vanish mid-deploy.
 */
type LifecyclePerm = {
  allowed: boolean;
  isAdmin: boolean;
  can?: { hold: boolean; cancel: boolean; reinstate: boolean; edit: boolean };
};

export default function IndentLifecycleControls({
  indentId, lifecycleState, lifecycle,
}: {
  indentId: string;
  lifecycleState?: 'active' | 'on_hold' | 'cancelled';
  lifecycle?: IndentLifecycle;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<null | 'hold' | 'cancel' | 'resume' | 'uncancel'>(null);
  const [text, setText] = useState('');

  const { data: perm } = useQuery<LifecyclePerm>({
    queryKey: ['procurement', 'indent-lifecycle-perm'],
    queryFn: () => api.get('/api/procurement/indents/lifecycle/can-control'),
    staleTime: 5 * 60_000,
  });

  const mayHold = perm?.can ? perm.can.hold : !!perm?.allowed;
  const mayCancel = perm?.can ? perm.can.cancel : !!perm?.allowed;
  const mayReinstate = perm?.can ? perm.can.reinstate : !!perm?.isAdmin;
  const mayAnything = mayHold || mayCancel || mayReinstate;

  const state = lifecycleState || 'active';
  const held = state === 'on_hold';
  const cancelled = state === 'cancelled';

  const run = async (action: 'hold' | 'resume' | 'cancel' | 'uncancel') => {
    const needsReason = action === 'hold' || action === 'cancel';
    if (needsReason && text.trim().length < 3) {
      toast.error(action === 'hold' ? 'Say why it is going on hold.' : 'Say why it is being cancelled.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/procurement/indents/${encodeURIComponent(indentId)}/${action}`,
        needsReason ? { reason: text.trim() } : { note: text.trim() });
      toast.success(
        action === 'hold' ? 'Indent put on hold'
          : action === 'cancel' ? 'Indent cancelled — it no longer counts toward spend'
            : action === 'uncancel' ? 'Indent reinstated' : 'Indent resumed',
      );
      setMode(null); setText('');
      await qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      await qc.invalidateQueries({ queryKey: ['procurement', 'dashboard'] });
    } catch (e: any) {
      toast.error(e?.message || 'Could not update the indent');
    } finally { setBusy(false); }
  };

  // Nothing to show a normal user on a normal indent. A held/cancelled one is
  // still worth explaining to everybody, so the banner stays.
  if (!mayAnything && state === 'active') return null;

  return (
    <div className="space-y-2">
      {held && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          <b>On hold</b>{lifecycle?.heldByName ? ` — put on hold by ${lifecycle.heldByName}` : ''}
          {lifecycle?.heldAt ? ` on ${fmt(lifecycle.heldAt)}` : ''}
          {lifecycle?.statusAtChange ? ` at "${lifecycle.statusAtChange}"` : ''}.
          {lifecycle?.holdReason ? <> Reason: {lifecycle.holdReason}</> : null}
        </div>
      )}
      {cancelled && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-900">
          <b>Cancelled</b>{lifecycle?.cancelledByName ? ` by ${lifecycle.cancelledByName}` : ''}
          {lifecycle?.cancelledAt ? ` on ${fmt(lifecycle.cancelledAt)}` : ''}
          {lifecycle?.statusAtChange ? ` at "${lifecycle.statusAtChange}"` : ''}.
          {lifecycle?.cancelReason ? <> Reason: {lifecycle.cancelReason}</> : null}
          <div className="mt-1 text-[11px] opacity-80">Excluded from committed and pending spend. Nothing was deleted.</div>
        </div>
      )}

      {mayAnything && (
        <div className="flex flex-wrap items-center gap-2">
          {state === 'active' && (
            <>
              {mayHold && <button disabled={busy} onClick={() => { setMode(mode === 'hold' ? null : 'hold'); setText(''); }}
                className="press rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-[12px] font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                Put on hold
              </button>}
              {mayCancel && <button disabled={busy} onClick={() => { setMode(mode === 'cancel' ? null : 'cancel'); setText(''); }}
                className="press rounded-lg border border-rose-400 bg-white px-3 py-1.5 text-[12px] font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                Cancel indent
              </button>}
            </>
          )}
          {held && (
            <>
              {mayHold && <button disabled={busy} onClick={() => run('resume')}
                className="press rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                Resume
              </button>}
              {mayCancel && <button disabled={busy} onClick={() => { setMode(mode === 'cancel' ? null : 'cancel'); setText(''); }}
                className="press rounded-lg border border-rose-400 bg-white px-3 py-1.5 text-[12px] font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                Cancel instead
              </button>}
            </>
          )}
          {cancelled && mayReinstate && (
            <button disabled={busy} onClick={() => { setMode(mode === 'uncancel' ? null : 'uncancel'); setText(''); }}
              className="press rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              Reinstate
            </button>
          )}
        </div>
      )}

      {mode && mode !== 'resume' && (
        <div className={
          'rounded-xl border p-3 ' +
          (mode === 'cancel' ? 'border-rose-300 bg-rose-50' : mode === 'hold' ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50')
        }>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-600">
            {mode === 'hold' ? 'Why is it going on hold?' : mode === 'cancel' ? 'Why is it being cancelled?' : 'Note (optional)'}
            {mode !== 'uncancel' && <span className="text-rose-500"> *</span>}
          </label>
          <input className="input w-full" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={mode === 'hold' ? 'e.g. client has paused the order pending budget' : mode === 'cancel' ? 'e.g. client withdrew the order' : 'Why it is coming back'} />
          {mode === 'cancel' && (
            <p className="mt-1.5 text-[11px] text-rose-800">
              The indent, its documents and its history are kept. It simply stops counting as money we intend to spend.
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button disabled={busy} onClick={() => { setMode(null); setText(''); }}
              className="press rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button disabled={busy} onClick={() => run(mode)}
              className={
                'press rounded-lg px-4 py-1.5 text-[12px] font-bold text-white disabled:opacity-50 ' +
                (mode === 'cancel' ? 'bg-rose-600 hover:bg-rose-700' : mode === 'hold' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-700 hover:bg-slate-800')
              }>
              {busy ? 'Saving…' : mode === 'hold' ? 'Put on hold' : mode === 'cancel' ? 'Cancel this indent' : 'Reinstate'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
